import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PassaporteSocial } from '../models/passaporte-public.model';

export interface ClientOwnerAccessResult {
  email: string;
  temporaryPassword: string;
  companyName: string;
  companyEnrichmentId: string;
}

export interface ClientOwnerPhoto {
  id: string;
  category: string;
  label: string;
  owned: boolean;
}

export interface ClientOwnerGallery {
  owned: boolean;
  photos: ClientOwnerPhoto[];
}

export interface ClientOwnerProfile {
  companyId: string;
  name: string;
  city: string | null;
  state: string | null;
  neighborhood: string | null;
  address: string | null;
  phone: string | null;
  whatsapp: string | null;
  hours: string | null;
  about: string | null;
  differentials: string[];
  suggestions: string[];
  caderno: string | null;
  nicho: string | null;
  atividade: string | null;
  tags: string[];
  amenities: string[];
  editable: boolean;
  lockReason: string | null;
  publicPath: string | null;
  hasPublicProfile: boolean;
  postalCode: string | null;
  differentialOptions: string[];
  gallery: ClientOwnerGallery;
  reservationsEnabled: boolean;
  reservationKinds: string[];
  /** true quando o setor (CNAE/nicho) já define o tipo de reserva. */
  reservationKindsLocked?: boolean;
  /** Tipos extras que o setor permite ligar (ex.: hotel com CNAE secundário de restaurante). */
  reservationOptionalKinds?: string[];
  /** Empresa publicada no Anuário/Passaporte (sem isso o cliente não vê nem reserva). */
  publishedInPassaporte?: boolean;
  faqs?: { question: string; answer: string }[];
  social?: PassaporteSocial | null;
  /** Setor ou tipos de reserva permitem montar cardápio. */
  restaurantMenu?: boolean;
}

export interface ClientOwnerProfileUpdate {
  name: string;
  postalCode: string;
  address: string;
  neighborhood: string;
  phone: string;
  whatsapp: string;
  hours: string;
  about: string;
  differentials: string[];
  faqs: { question: string; answer: string }[];
  instagram: string;
  facebook: string;
  tripAdvisor: string;
}

@Injectable({ providedIn: 'root' })
export class ClientOwnerService {
  private readonly http = inject(HttpClient);
  private readonly profileState = signal<ClientOwnerProfile | null>(null);

  readonly reservationsEnabled = signal(false);
  readonly reservationKinds = signal<string[]>([]);
  readonly reservationKindsLocked = signal(false);
  readonly reservationOptionalKinds = signal<string[]>([]);
  readonly publishedInPassaporte = signal(true);
  readonly restaurantMenu = signal(false);

  hasProfile(): boolean {
    return this.profileState() !== null;
  }

  mine() {
    return this.http.get<ClientOwnerProfile>(`${environment.apiBaseUrl}/minha-empresa`).pipe(
      tap((profile) => this.remember(profile))
    );
  }

  save(payload: ClientOwnerProfileUpdate) {
    return this.http.put<ClientOwnerProfile>(`${environment.apiBaseUrl}/minha-empresa`, payload).pipe(
      tap((profile) => this.remember(profile))
    );
  }

  updateReservationKinds(kinds: string[]) {
    return this.http
      .put<ClientOwnerProfile>(`${environment.apiBaseUrl}/minha-empresa/reservas/tipos`, { kinds })
      .pipe(tap((profile) => this.remember(profile)));
  }

  private remember(profile: ClientOwnerProfile): void {
    this.profileState.set(profile);
    this.reservationsEnabled.set(!!profile.reservationsEnabled);
    this.reservationKinds.set(profile.reservationKinds ?? []);
    this.reservationKindsLocked.set(!!profile.reservationKindsLocked);
    this.reservationOptionalKinds.set(profile.reservationOptionalKinds ?? []);
    this.publishedInPassaporte.set(profile.publishedInPassaporte !== false);
    this.restaurantMenu.set(!!profile.restaurantMenu);
  }

  photoBlob(photoId: string) {
    return this.http.get(`${environment.apiBaseUrl}/minha-empresa/fotos/${photoId}`, { responseType: 'blob' });
  }

  addPhoto(file: File) {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<ClientOwnerGallery>(`${environment.apiBaseUrl}/minha-empresa/fotos`, body);
  }

  removePhoto(photoId: string) {
    return this.http.delete<ClientOwnerGallery>(`${environment.apiBaseUrl}/minha-empresa/fotos/${photoId}`);
  }

  makeCover(photoId: string) {
    return this.http.post<ClientOwnerGallery>(`${environment.apiBaseUrl}/minha-empresa/fotos/${photoId}/capa`, {});
  }

  renamePhoto(photoId: string, label: string) {
    return this.http.put<ClientOwnerGallery>(`${environment.apiBaseUrl}/minha-empresa/fotos/${photoId}`, { label });
  }

  grant(companyId: string, payload: { name: string; email: string; password?: string }) {
    return this.http.post<ClientOwnerAccessResult>(
      `${environment.apiBaseUrl}/clients/${companyId}/acesso-empresario`,
      payload
    );
  }
}
